import { Component, ElementRef, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { WidgetParent } from "../../class/widget-parent";
import { ComponentAdapterFactory } from "../../class/adapters/component-adapter-factory";
import { ComponentAdaperDimmer } from "../../class/adapters/component-adapter-dimmer";
import { SmartPaletteGeneral } from "../../class/smart-palette";
import { Subscription } from "rxjs";
import { debounceTime, filter } from "rxjs/operators";
import type { Animation } from '@ionic/angular';
import { IconHelper } from "../../class/icon-helper";
import { IonButton, IonIcon } from "@ionic/angular";
import { DraggableSliderComponent } from "../draggable-slider/draggable-slider.component";
import { SmartComponentSpinnerComponent } from "../smart-component-spinner/smart-component-spinner.component";
import { ErrorLoadingWidgetComponent } from "../error-loading-widget/error-loading-widget.component";

@Component({
    selector: "app-widget-dimmer",
    templateUrl: "./widget-dimmer.component.html",
    styleUrls: [
        "./widget-dimmer.component.scss",
        "../../styles/shared-global.scss",
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [IonButton, IonIcon, DraggableSliderComponent, SmartComponentSpinnerComponent, ErrorLoadingWidgetComponent]
})

/**
 * Counter entry componet
 */
export class WidgetDimmerComponent extends WidgetParent<ComponentAdaperDimmer> {
  private _faultStateAnimation: Animation;
  private _widgetIsDisplayableSub: Subscription = null;

  private _iconDimmerOn = "assets/img/dimmer/ico-dimmer-on-no-bg.svg";
  private _iconDimmerOff = "assets/img/dimmer/ico-dimmer-off-no-bg.svg";

  public brightnessValue: number = 0;
  public iconDimmerState: string = "";
  public titleWidget: string = "";
  public allowBrightnessChange: boolean = false;
  public faultState: boolean = false;

  public iconHelper = IconHelper

  @ViewChild("faultStateIcon", { read: ElementRef }) _faultStateIcon: ElementRef;

  constructor() {
    super();

    this._iconDimmerOn = IconHelper.getIconsForDimmer().on;
    this._iconDimmerOff = IconHelper.getIconsForDimmer().off;

    this.iconDimmerState = this._iconDimmerOff;
  }

  public ngAfterViewInit(): void {

    this._widgetIsDisplayableSub = this._widgetIsDisplayable.pipe(
      filter(isReady => isReady),
      debounceTime(500),
    ).subscribe(isReady => {

      //animate icon for fault state
      if (this.faultState) {
        this.createFaultStateAnimation();
      }
    });
  }

  /**
   * Add class "turn-on" to button if dimmer is on
   * @returns boolean
   */
  public toggleClassForButton(): boolean {
    return this.iconDimmerState === this._iconDimmerOn ? true : false;
  }

  /**
   * Callback for slider value change
   *
   * @param progressValue Value to set
   */
  public onSlideDimmer(progressValue: number) {
    if (this.componentAdapter.BrighnessCommand !== null) {
      this.sendBrightnessCommand(progressValue);
    }
  }

  /**
   * When the button is clicked turn on/off light
   */
  public onClickSwitchDimmer(): void {
    this.switchDimmer();
  }

  /**
   * Switch light on/off
   */
  private switchDimmer() {
    if (this.componentAdapter.TurnOnOffCommand !== null) {
      this.sendTurnOnOffCommand();
    }
  }

  protected setWidgetSmartPalette(): SmartPaletteGeneral {
    let widgetSmaertPalette = new SmartPaletteGeneral();

    return widgetSmaertPalette;
  }

  protected onComponentLoad(): void { }

  protected onComponentDestroy(): void {
    this._faultStateAnimation?.stop();
    this._faultStateAnimation = null;
    this._widgetIsDisplayableSub?.unsubscribe();
  }

  protected onSmartComponentErrorLoading(): void { }

  protected onSmartComponentSuccessLoaded(): void | boolean {
    const factory = new ComponentAdapterFactory();
    this.componentAdapter = factory.createComponentAdapter(
      this._listSmartComponentSkills,
      ComponentAdaperDimmer
    );

    this.titleWidget = this.componentAdapter.Name;

    //If component title is empty show error loading component
    if (!this.titleWidget) {
      return this._errorLoading = true;
    }

    this.allowBrightnessChange =
      this.componentAdapter.BrighnessCommand !== null;
  }

  protected onAddressValueChange(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (this.componentAdapter.TurnOnOffState) {
      this.tryUpdateIconDimmerState(instanceGUID, address, value);
    }

    if (this.componentAdapter.BrighnessState) {
      this.tryUpdateProgressValue(instanceGUID, address, value);
    }

    if (this.componentAdapter.FaultState) {
      this.tryUpdateFaultState(instanceGUID, address, value);
    }

    //animate fault state icon
    if (this.faultState) {
      setTimeout(() => this.createFaultStateAnimation(), 500);
    } else {
      this._faultStateAnimation?.stop();
      this._faultStateAnimation = null;
    }
  }

  /**
   * Send brightness command to MQTT with percentege value (0 / 100%)
   *
   * @param value Value to set over brightness address
   */
  private sendBrightnessCommand(value: number) {
    const instanceGUID: string =
      this.componentAdapter.BrighnessCommand.instanceGUID;
    const address: string = this.componentAdapter.BrighnessCommand.address;
    this.brightnessValue = value;

    // Send command to turn on/off the light using VALUE NOT command
    this.sendAddressCommand(
      instanceGUID,
      address,
      this.brightnessValue.toString()
    );
  }

  /**
   * Send turn on/off command to MQTT
   */
  private sendTurnOnOffCommand() {
    const instanceGUID: string =
      this.componentAdapter.TurnOnOffCommand.instanceGUID;
    const address: string = this.componentAdapter.TurnOnOffCommand.address;

    // Send command to turn on/off the light using VALUE NOT command
    this.sendAddressCommand(instanceGUID, address, "VALUE NOT");
  }

  /**
   * Try to update the icon dimmer state based on the value of the address
   *
   * @param instanceGUID Instance GUID of the address changed
   * @param address Address received
   * @param value Value received
   */
  private tryUpdateIconDimmerState(
    instanceGUID: string,
    address: string,
    value: string
  ) {
    if (
      instanceGUID === this.componentAdapter.TurnOnOffState.instanceGUID &&
      address === this.componentAdapter.TurnOnOffState.address
    ) {
      if (value === "1") {
        this.iconDimmerState = this._iconDimmerOn;
      } else {
        this.iconDimmerState = this._iconDimmerOff;
      }
    }
  }

  /**
   * Try to update the progress value based on the value of the address
   *
   * @param instanceGUID Instance GUID of the address changed
   * @param address Address received
   * @param value Value received
   */
  private tryUpdateProgressValue(
    instanceGUID: string,
    address: string,
    value: string
  ) {
    if (
      instanceGUID === this.componentAdapter.BrighnessState.instanceGUID &&
      address === this.componentAdapter.BrighnessState.address &&
      value !== ""
    ) {
      this.brightnessValue = parseFloat(value);
    }
  }

  /**
   * Try to update fault state of the dimmer
   *
   * @param instanceGUID Instance GUID of the address changed
   * @param address Address received
   * @param value Value received
   */
  private tryUpdateFaultState(
    instanceGUID: string,
    address: string,
    value: string
  ) {
    if (
      instanceGUID === this.componentAdapter.FaultState.instanceGUID &&
      address === this.componentAdapter.FaultState.address
    ) {
      this.faultState = parseInt(value) === 1;
    }
  }

  /**
   * Create animation for fault state icon
   */
  private createFaultStateAnimation(): void {
    this._faultStateAnimation = this._animationController
      .create()
      .addElement(this._faultStateIcon.nativeElement)
      .duration(1500)
      .iterations(Infinity)
      .direction("alternate")
      .fromTo('opacity', 1, 0.3);

    this._faultStateAnimation.play();
  }
}
